import React, { useState } from "react";
import { Box, Typography, Modal, IconButton } from "@material-ui/core";
import { FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";
import { styled } from "@material-ui/styles";
import {
  StyledBottomImage,
  StyledImageDrop,
  StyledImagePreview,
} from "../styles";

const StyledImageContainer = styled(Box)`
  position: relative;
  width: 100%;
  height: 300px;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledMainImage = styled("img")`
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
`;

const StyledThumbnailContainer = styled(Box)`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  overflow-x: auto;
  padding: 4px 0;
`;

const StyledThumbnail = styled(Box)`
  max-width: 10px;
  max-height: 10px;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid ${(props) => (props.isActive ? "#1976d2" : "transparent")};
  transition: border-color 0.2s;

  &:hover {
    border-color: #1976d2;
  }
`;

const StyledThumbnailImage = styled("img")`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StyledModal = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const StyledModalContent = styled(Box)`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  outline: none;
`;

const StyledModalImage = styled("img")`
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 8px;
`;

const StyledModalButton = styled(IconButton)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  z-index: 1;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
`;

const StyledCloseButton = styled(IconButton)`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  z-index: 1;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
`;

export const ItemImageDisplay = ({ images = [], alt = "Item image" }) => {
  const [activeImage, setActiveImage] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);

  // Convert base64 strings to data URLs for display
  const getImageUrl = (imageData) => {
    if (!imageData) return "";
    return imageData.startsWith("data:")
      ? `data:image/jpeg;base64,${imageData}`
      : imageData;
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const nextImage = () => {
    setModalImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setModalImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowRight") nextImage();
    if (event.key === "ArrowLeft") prevImage();
    if (event.key === "Escape") closeModal();
  };

  if (!images || images.length === 0) {
    return (
      <Box px={3} py={2}>
        <StyledImageContainer>
          <StyledImageDrop>
            <StyledImagePreview
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "150px",
              }}
            >
              {" "}
              <Typography variant="body2" color="text.secondary">
                No images available
              </Typography>
            </StyledImagePreview>
          </StyledImageDrop>
        </StyledImageContainer>
      </Box>
    );
  }

  const imageList = images.map((image, index) => {
    // Convert base64 string back to data URL for display
    const imageUrl = image.startsWith("data:")
      ? image
      : `data:image/jpeg;base64,${image}`;

    return (
      <StyledBottomImage
        key={index}
        onClick={() => setActiveImage(index)}
        isActive={activeImage === index}
      >
        <StyledThumbnailImage
          src={getImageUrl(image)}
          alt={`${alt} thumbnail ${index + 1}`}
        />
      </StyledBottomImage>
    );
  });

  return (
    <>
      <Box px={3} py={2}>
        {/* Main Image Display */}
        <StyledImageDrop>
          <StyledImagePreview>
            <img src={getImageUrl(images[activeImage])} alt="Preview" />
          </StyledImagePreview>
        </StyledImageDrop>

        {/* Thumbnail Navigation */}

        <Box mt={3} px={3} display="flex" alignItems="center" gap={1}>
          {imageList}
        </Box>

        {/* Image Counter */}
        {images.length > 1 && (
          <Box mt={1} display="flex" justifyContent="center">
            <Typography variant="caption" color="text.secondary">
              {activeImage + 1} of {images.length}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Full Screen Modal */}
      <StyledModal
        open={modalOpen}
        onClose={closeModal}
        onKeyDown={handleKeyDown}
      >
        <StyledModalContent>
          <StyledCloseButton onClick={closeModal}>
            <FiX />
          </StyledCloseButton>

          {images.length > 1 && (
            <>
              <StyledModalButton onClick={prevImage} style={{ left: 10 }}>
                <FiChevronLeft />
              </StyledModalButton>

              <StyledModalButton onClick={nextImage} style={{ right: 10 }}>
                <FiChevronRight />
              </StyledModalButton>
            </>
          )}
          {images.length > 1 && (
            <Box
              position="absolute"
              bottom={20}
              left="50%"
              transform="translateX(-50%)"
              background="rgba(0, 0, 0, 0.7)"
              color="white"
              px={2}
              py={1}
              borderRadius={1}
            >
              <Typography variant="caption">
                {modalImageIndex + 1} of {images.length}
              </Typography>
            </Box>
          )}
        </StyledModalContent>
      </StyledModal>
    </>
  );
};
